import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { HStack } from "@design-system/HStack/HStack";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    ASSIGNEE_ROLE_LABEL,
    ASSIGNEE_ROLE_OPTIONS,
} from "../closeTemplateTasksDrawer.constants";
import { ServiceTemplateScheduleOccurrenceRow } from "../serviceTemplateScheduleOccurrencesViewConfig";
import {
    ServiceTemplateScheduleOccurrenceCallbacks,
    ServiceTemplateScheduleOccurrenceTableOptions,
} from "../serviceTemplateScheduleOccurrenceCallbacks";
import { EMPTY_TEMPLATE_ESTIMATE_STATS } from "../templateEstimateStats";
import styles from "./ServiceTemplateScheduleOccurrenceEstimatedTimeCell.module.scss";

export const ServiceTemplateScheduleOccurrenceEstimatedTimeCell = memo(
    ({
        table,
    }: CellContext<
        ViewTableFeatures,
        ServiceTemplateScheduleOccurrenceRow,
        unknown
    >) => {
        const { options } = useViewTableMeta<
            ServiceTemplateScheduleOccurrenceCallbacks,
            ServiceTemplateScheduleOccurrenceTableOptions
        >(table);

        const stats =
            options?.templateEstimateStats ?? EMPTY_TEMPLATE_ESTIMATE_STATS;
        const { totalEstimatedTime, estimatedTimeByRole } = stats;

        const roleBreakdown = ASSIGNEE_ROLE_OPTIONS.filter(
            (role) => (estimatedTimeByRole[role] ?? 0) > 0,
        );

        const formattedTotal = formatDuration(totalEstimatedTime) || "-";

        if (totalEstimatedTime === 0) {
            return (
                <ViewTableCell className={styles.cell}>
                    <Tooltip content="No task estimates on template">
                        <span className={styles.empty}>-</span>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        if (roleBreakdown.length <= 1) {
            return (
                <ViewTableCell className={styles.cell}>
                    {formattedTotal}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell className={styles.cell}>
                <Tooltip
                    side="bottom"
                    content={
                        <VStack gap={4} className={styles.breakdown}>
                            {roleBreakdown.map((role) => (
                                <HStack key={role} gap={12} justify="between">
                                    <span>{ASSIGNEE_ROLE_LABEL[role]}</span>
                                    <span>
                                        {formatDuration(
                                            estimatedTimeByRole[role],
                                        )}
                                    </span>
                                </HStack>
                            ))}
                        </VStack>
                    }
                >
                    <span>{formattedTotal}</span>
                </Tooltip>
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleOccurrenceEstimatedTimeCell.displayName =
    "ServiceTemplateScheduleOccurrenceEstimatedTimeCell";
